import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../api/created/changeLog.ts';
import { ChangeLogSection } from '../../../../api/created/changeLog.ts';

const CASE_MANAGEMENT_CHANGE_FIELD_ORDER = [
  'salutation',
  'first_name',
  'last_name',
  'birth_date',
  'gender',
  'mobile_phone_number',
  'private_phone_number',
  'work_phone_number',
  'email',
  'nationality_country_code',
  'origin_country_code',
  'arrival',
  'residency_permit',
  'asylum_number',
  'zemis_number',
  'languages',
  'additional_information',
  'street',
  'postal_code',
  'city',
  'state',
  'copy_organization',
  'copy_address_type',
  'copy_address',
  'copy_zusatz',
  'copy_zip',
  'copy_city',
  'copy_canton',
  'copy_phone',
  'copy_email',
  'copy_homepage',
  'copy_contact_first_name',
  'copy_contact_last_name',
  'copy_contact_function',
  'copy_contact_phone',
  'copy_contact_email',
  'relation_type',
  'living_status',
  'note',
] as const;

const DIAGNOSTICS_CHANGE_FIELD_ORDER = [
  'affected_organ',
  'other_organ_name',
  'clinical_test_material',
  'lab_test_material',
  'other_material',
  'clinical_detection_method',
  'lab_detection_method',
  'lab_order_test_type',
  'lab_order_result_type',
  'sampling_date',
  'sampling_time',
  'sampling_time_of_day',
  'external_id',
  'result',
  'test_result',
  'result_interpretation',
  'result_note',
  'species',
  'resistance',
  'resistance_name',
  'enzyme',
  'mutation_status',
  'referenceValue',
  'unit',
  'notes',
  'note',
  'SPUTUM_MATERIAL_SPUTUM',
  'SPUTUM_MATERIAL_AIRWAYS',
  'SPUTUM_MATERIAL_CUSTOM',
  'SPUTUM_METHOD_SPUTUM_MICROSCOPICAL',
  'SPUTUM_METHOD_AIRWAYS_MICROSCOPICAL',
  'SPUTUM_METHOD_CUSTOM_MICROSCOPICAL',
  'SPUTUM_METHOD_SPUTUM_MOLECULAR',
  'SPUTUM_METHOD_AIRWAYS_MOLECULAR',
  'SPUTUM_METHOD_CUSTOM_MOLECULAR',
  'SPUTUM_METHOD_SPUTUM_CULTURE',
  'SPUTUM_METHOD_AIRWAYS_CULTURE',
  'SPUTUM_METHOD_CUSTOM_CULTURE',
  'XRAY_RX_NUMBER',
  'XRAY_FINDING_DOCTOR',
  'XRAY_FINDING',
  'XRAY_FINDING_COMMENT',
  'XRAY_FOLLOWUP',
  'XRAY_FILE_LINK',
  'TUBERCULIN_INDURATION',
  'TUBERCULIN_READ_TIME',
  'QUANTIFERON_NIL_NOT_STIMULATED',
  'QUANTIFERON_MITOGEN',
  'QUANTIFERON_TB_ANTIGEN_1',
  'QUANTIFERON_TB_ANTIGEN_2',
  'QUANTIFERON_TB_WERT_1',
  'QUANTIFERON_TB_WERT_2',
  'QUANTIFERON_FINDING',
  'TSPOT_ESAT6',
  'TSPOT_CFP10',
] as const;

const THERAPY_CHANGE_FIELD_ORDER = [
  'start_phase_1',
  'start_phase_2',
  'end',
  'finished',
  'therapy_result',
  'result_note',
  'condition_interval',
  'therapy_condition',
  'therapy_compliance',
  'directly_observed_therapy_needed',
  'direct_payer',
  'tuberculosis_fund',
  'date',
  'entry_date',
  'supplement_date',
  'PATIENT_HOSPITAL',
  'PATIENT_ISOLATION',
  'notes',
  'note',
] as const;

export const PATIENT_HISTORY_CHANGE_FIELD_ORDER_BY_SECTION = {
  [ChangeLogSection.CASE_MANAGEMENT]: CASE_MANAGEMENT_CHANGE_FIELD_ORDER,
  [ChangeLogSection.DIAGNOSTICS]: DIAGNOSTICS_CHANGE_FIELD_ORDER,
  [ChangeLogSection.THERAPY]: THERAPY_CHANGE_FIELD_ORDER,
} satisfies Partial<Record<ChangeLogSection, readonly string[]>>;

function getPatientHistoryChangeOrder(section: string | null) {
  switch (section) {
    case ChangeLogSection.CASE_MANAGEMENT:
      return CASE_MANAGEMENT_CHANGE_FIELD_ORDER;
    case ChangeLogSection.DIAGNOSTICS:
      return DIAGNOSTICS_CHANGE_FIELD_ORDER;
    case ChangeLogSection.THERAPY:
      return THERAPY_CHANGE_FIELD_ORDER;
    default:
      return undefined;
  }
}

function normalizeOrderKey(value: string | null | undefined) {
  return value?.trim().toLowerCase() ?? '';
}

function getRankMap(order: readonly string[]) {
  return order.reduce<Map<string, number>>((rankMap, value, index) => {
    const normalizedValue = normalizeOrderKey(value);
    if (normalizedValue && !rankMap.has(normalizedValue)) {
      rankMap.set(normalizedValue, index);
    }

    return rankMap;
  }, new Map<string, number>());
}

function getFirstRank(rankMap: Map<string, number>, candidates: (string | null | undefined)[]) {
  for (const candidate of candidates) {
    const rank = rankMap.get(normalizeOrderKey(candidate));
    if (rank !== undefined) {
      return rank;
    }
  }

  return Number.MAX_SAFE_INTEGER;
}

function getChangeSortPosition(change: ChangeLogFieldChangeDto, rankMap: Map<string, number>) {
  const exactRank = getFirstRank(rankMap, [
    `${change.entityType}.${change.parentSubType}.${change.subType}.${change.fieldName}`,
    `${change.entityType}.${change.subType}.${change.fieldName}`,
    `${change.parentSubType}.${change.subType}.${change.fieldName}`,
    `${change.subType}.${change.fieldName}`,
    `${change.fieldName}.${change.subType}`,
  ]);

  const fieldRank = getFirstRank(rankMap, [change.fieldName]);
  const subTypeRank = getFirstRank(rankMap, [change.subType, change.parentSubType]);

  return {
    primary: Math.min(exactRank, fieldRank),
    secondary: subTypeRank,
  };
}

export function sortPatientHistoryChanges(entry: ChangeLogEntryDto) {
  const order = getPatientHistoryChangeOrder(entry.section);

  if (!order) {
    return entry.changes ?? [];
  }

  const rankMap = getRankMap(order);

  return [...(entry.changes ?? [])]
    .map((change, index) => ({
      change,
      index,
      position: getChangeSortPosition(change, rankMap),
    }))
    .sort((a, b) => {
      const primaryDiff = a.position.primary - b.position.primary;
      if (primaryDiff !== 0) {
        return primaryDiff;
      }

      const secondaryDiff = a.position.secondary - b.position.secondary;
      if (secondaryDiff !== 0) {
        return secondaryDiff;
      }

      return a.index - b.index;
    })
    .map(({ change }) => change);
}
